import { ObjectBuilder, Image3D, MeshUtils, Rectangle3D, ScrollViewer3D } from "@dreamcam/babylon-tools";
import { Env } from "../../settings/Env";
import { Container } from "@babylonjs/gui/2D/controls/container.js";
import { text } from "../../TextResources";
import { DcRenderingGroup } from "../DcRenderingGroup";
import { XRInputHelper } from "../XRInputHelper";
import { AppView } from "../AppView";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel.js";
import { MessengerChatListLayoutHelper } from "./MessengerChatListLayoutHelper";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { ScrollHandler } from "../ScrollHandler";
import { BroadcastApiStatus } from "@dreamcam/web-sdk";
import { DateUtils } from "../../utils/Time";
import { StringUtils } from "../utils/StringUtils";
import { AvatarStatus } from "./AvatarStatus";
import { simpleHash } from "../../utils/HashUtils";
const getChatListHash = (chats) => {
    const ids = chats.map(chat => chat.id).join(",");
    return simpleHash(ids);
};
export class MessengerChatListPanel {
    get mesh2d() { return this._mesh2d; }
    constructor(mesh2d) {
        this._onHideCallback = null;
        this._allItems = [];
        this._unpinnedItems = [];
        this._lastChatListState = undefined;
        this._buttonHandler = (button) => {
            setTimeout(() => {
                if (this._messengerDataCallbacks.isMessageLoadingInProgress())
                    return;
                const buttonModelId = button.modelId;
                this._messengerDataCallbacks.setSelectedModelId(buttonModelId);
            });
        };
        this._mesh2d = mesh2d;
        this._controls = this._createContent(this._mesh2d.texture);
    }
    static createMessengerChatListPanel(params) {
        const cp = Env.common.messengerChatListPanel;
        const scene = AppView.getInstance().scene;
        const mesh2d = MeshUtils.createPlaneMesh(params.messengerChatListMeshName, cp.background.width, cp.background.height, scene, params.parentNode, DcRenderingGroup.topDialogs);
        mesh2d.mesh.position = params.messengerChatListPosition;
        return new MessengerChatListPanel(mesh2d);
    }
    hide() {
        if (MeshUtils.isHidden(this._mesh2d))
            return;
        MeshUtils.hide(this._mesh2d);
        if (this._onHideCallback) {
            this._onHideCallback();
            this._onHideCallback = null;
        }
    }
    show(onHideCallback) {
        this._onHideCallback = onHideCallback;
        MeshUtils.show(this._mesh2d);
    }
    get isVisible() {
        return this._mesh2d.mesh.isVisible;
    }
    set isVisible(value) {
        if (this.isVisible === value)
            return;
        MeshUtils.setVisibility(this._mesh2d, value);
    }
    setMessengerDataCallbacks(callbacks) {
        this._messengerDataCallbacks = callbacks;
    }
    async setPinnedModelIdAsync(modelId) {
        this._messengerDataCallbacks.setPinnedModelId(modelId);
        this._controls.pinnedChatControls.chatControls.modelButton.modelId = modelId;
        const hasPinnedChat = modelId !== null;
        this._controls.pinnedChatControls.rootContainer.isVisible = hasPinnedChat;
        this._controls.chatListControls.topGap.isVisible = !hasPinnedChat;
        const sv = Env.common.messengerChatListPanel.body.chatList.scrollView;
        this._controls.chatListControls.scrollViewer.heightInPixels =
            this._mesh2d.m2px(hasPinnedChat ? sv.shortHeight : sv.fullHeight);
        await this._messengerDataCallbacks.createChatAsync(modelId);
        this.outputMessengerChats(this._messengerDataCallbacks.getChatList());
    }
    setSelectedModelId(modelId) {
        this._messengerDataCallbacks.setSelectedModelId(modelId);
    }
    outputMessengerSelectedModel(modelId) {
        this._allItems.forEach((x) => {
            const selected = modelId !== null && modelId === x.modelId;
            const button = x.chatControls.modelButton;
            button.isEnabled = !selected;
            button.onPointerOutObservable.notifyObservers(button, -1, button, button); // the patch to fix missing state change
        });
    }
    outputMessengerModelStatuses(modelStatuses) {
        this._unpinnedItems.forEach((item) => {
            const status = modelStatuses.find(x => x.userId === item.modelId)?.status ?? BroadcastApiStatus.OFFLINE;
            const avatarData = AvatarStatus.broadcastApiStatusMapForMessenger[status];
            item.chatControls.avatar.outlineContainer.color = avatarData.color;
            item.chatControls.avatar.statusTextContainer.background = avatarData.color;
            item.chatControls.avatar.statusText.text = avatarData.text;
        });
    }
    outputMessengerChats(chatList) {
        const chatListHash = getChatListHash(chatList);
        const pinnedModelId = this._messengerDataCallbacks.getPinnedModelId();
        const chatListChanged = this._lastChatListState === undefined ||
            this._lastChatListState.chatListHash !== chatListHash ||
            this._lastChatListState.pinnedModelId !== pinnedModelId;
        if (!chatListChanged)
            return;
        this._lastChatListState = { chatListHash, pinnedModelId };
        const chatsStackPanel = this._controls.chatListControls.chatsStackPanel;
        while (chatsStackPanel.children.length > 0) {
            const child = chatsStackPanel.children.shift();
            child?.dispose();
        }
        this._unpinnedItems.length = 0;
        this._allItems.length = 0;
        chatsStackPanel.addControl(this._controls.chatListControls.topGap);
        for (const chat of chatList) {
            const chatModelId = chat.participant.user_id;
            if (chatModelId === null)
                continue;
            let chatListItem;
            const isPinnedChat = pinnedModelId !== null && pinnedModelId === chatModelId;
            if (isPinnedChat) {
                chatListItem = { modelId: chatModelId, chat, chatControls: this._controls.pinnedChatControls.chatControls };
                this._outputMessengerPinnedChat(chat, this._controls.pinnedChatControls.chatControls);
            }
            else {
                const chatControls = MessengerChatListLayoutHelper.createChatView({ mesh2d: this._mesh2d, chatId: chat.id });
                chatsStackPanel.addControl(chatControls.rootContainer);
                chatControls.modelButton.modelId = chatModelId;
                chatControls.modelButton.onClickObserver.clear();
                chatControls.modelButton.onClickObserver.add(this._buttonHandler);
                chatListItem = { modelId: chatModelId, chat, chatControls };
                this._unpinnedItems.push(chatListItem);
                this._outputMessengerChat(chat, chatControls);
            }
            this._allItems.push(chatListItem);
        }
        chatsStackPanel.addControl(this._controls.chatListControls.bottomGap);
        this.outputMessengerModelStatuses(this._messengerDataCallbacks.getModelStatuses());
        setTimeout(() => {
            this._controls.chatListControls.scrollViewer.verticalValue = 0;
        });
    }
    outputMessengerChatChanged(args) {
        const chat = args.chatData;
        const pinnedModelId = this._messengerDataCallbacks.getPinnedModelId();
        for (let i = 0; i < this._allItems.length; i++) {
            const chatListItem = this._allItems[i];
            if (chatListItem.modelId === chat.participant.user_id) {
                chatListItem.chat = chat;
                const isPinnedChat = pinnedModelId !== null && pinnedModelId === chatListItem.modelId;
                if (isPinnedChat) {
                    this._outputMessengerPinnedChat(chat, chatListItem.chatControls);
                }
                else {
                    this._outputMessengerChat(chat, chatListItem.chatControls);
                }
                break;
            }
        }
    }
    outputMessengerModelTypingChanged(chatId, _isTyping) {
        const chatListItem = this._allItems.find(x => x.chat?.id === chatId);
        if (chatListItem) {
            this._outputMessengerPinnedChat(chatListItem.chat, chatListItem.chatControls);
        }
    }
    outputMessengerIsFavoriteChanged(modelId, isFavorite) {
        const chatListItem = this._unpinnedItems.find(x => x.modelId === modelId);
        if (chatListItem) {
            this._outputMessengerIsFavorite(isFavorite, chatListItem.chatControls);
        }
    }
    _outputMessengerPinnedChat(chat, chatControls) {
        const it = Env.common.messengerChatListPanel.item;
        const userClientId = this._messengerDataCallbacks.getUserClientId();
        const hasUnread = chat.unread_count > 0;
        const isSent = !hasUnread && chat.last_message !== null && chat.last_message.sender_id === userClientId;
        const isRead = !hasUnread && isSent && chat.last_message !== null && chat.last_message.read_at !== null;
        const statusIconWidthInPixels = this._mesh2d.m2px(it.topLine.statusIcon.width);
        chatControls.sentIcon.isVisible = isSent;
        chatControls.sentIcon.widthInPixels = isSent ? statusIconWidthInPixels : 0;
        chatControls.readIcon.isVisible = isRead;
        chatControls.readIcon.widthInPixels = isRead ? statusIconWidthInPixels : 0;
        chatControls.unreadNotificationIcon.isVisible = hasUnread;
        chatControls.unreadNotificationIcon.widthInPixels = hasUnread ? statusIconWidthInPixels : 0;
        // Note: chatControls.favoriteStarImage.parent container is a common parent for
        //       sentIcon, readIcon, unreadNotificationIcon, and favoriteStarImage controls.
        chatControls.favoriteStarImage.parent.markAsDirty();
        chatControls.lastMessageTimeTextBlock.text = chat.last_message === null
            ? ""
            : DateUtils.messageTimeToString(chat.last_message.sent_at);
        const tipsAmount = chat.last_message?.attachments.find(x => x.type === "tip")?.meta.amount;
        const hasDraft = chat.draft_message.content !== "";
        const isTyping = this._messengerDataCallbacks.getIsModelTyping(chat.id);
        const hasMessage = chat.last_message?.content !== "";
        const hasTips = !hasMessage && tipsAmount !== undefined && tipsAmount > 0;
        const isBanned = chat.participant.reverse_block_data.is_blocked;
        let statusText;
        if (isBanned) {
            statusText = text.common_messenger_chatlist_banned;
        }
        else if (isTyping) {
            statusText = text.common_messenger_model_status_typing;
        }
        else if (hasDraft) {
            statusText = StringUtils.stringFormat(text.common_messenger_chatlist_draft, it.status.draft.color, chat.draft_message.content);
        }
        else if (hasTips) {
            statusText = StringUtils.stringFormat(text.common_messenger_chatlist_tips_richtext, it.status.tips.color, tipsAmount);
        }
        else if (hasMessage) {
            statusText = chat.last_message?.content ?? text.common_messenger_chatlist_no_messages;
        }
        else {
            statusText = text.common_messenger_chatlist_no_messages;
        }
        chatControls.statusTextBlock.text = statusText;
    }
    _outputMessengerChat(chat, chatControls) {
        chatControls.modelButton.isEnabled = chat.participant.user_id !== this._messengerDataCallbacks.getSelectedModelId();
        const isChatAvailable = !chat.participant.reverse_block_data.is_blocked;
        chatControls.avatar.rootContainer.isVisible = isChatAvailable;
        const avatarUrl = chat.participant.avatar ?? Env.lobby.gallery.defaultPreview.path;
        chatControls.avatar.modelProfileImage.setSource(avatarUrl, Env.lobby.gallery.defaultPreview.path);
        chatControls.unavailableAvatar.isVisible = !isChatAvailable;
        chatControls.unavailableAvatarLetter.isVisible = !isChatAvailable;
        const modelNickname = chat.participant.username ?? "";
        chatControls.unavailableAvatarLetter.text = modelNickname.length > 0 ? modelNickname[0] : "";
        chatControls.modelNicknameTextBlock.text = modelNickname;
        this._outputMessengerIsFavorite(chat.participant.is_favorite === true, chatControls);
        this._outputMessengerPinnedChat(chat, chatControls);
    }
    _outputMessengerIsFavorite(isFavorite, chatControls) {
        const it = Env.common.messengerChatListPanel.item;
        chatControls.favoriteStarImage.isVisible = isFavorite;
        chatControls.favoriteStarImage.widthInPixels = isFavorite ? this._mesh2d.m2px(it.topLine.star.width) : 0;
        chatControls.favoriteStarImage.parent.markAsDirty();
    }
    outputPinnedModelNickname(_id, modelNickname) {
        this._controls.pinnedChatControls.chatControls.modelNicknameTextBlock.text = modelNickname;
    }
    outputPinnedIsFavorite(isFavorite) {
        const chatControls = this._controls.pinnedChatControls.chatControls;
        const it = Env.common.messengerChatListPanel.item;
        chatControls.favoriteStarImage.isVisible = isFavorite;
        chatControls.favoriteStarImage.widthInPixels = isFavorite ? this._mesh2d.m2px(it.topLine.star.width) : 0;
        chatControls.favoriteStarImage.parent.markAsDirty();
    }
    overrideChatAvatar(modelId, url) {
        const chatListItem = this._allItems.find(x => x.modelId === modelId);
        if (!chatListItem)
            return;
        const chatControls = chatListItem.chatControls;
        chatControls.avatar.rootContainer.isVisible = true;
        chatControls.avatar.modelProfileImage.setSource(url, Env.lobby.gallery.defaultPreview.path);
    }
    outputPinnedProfilePhoto(url) {
        const chatControls = this._controls.pinnedChatControls.chatControls;
        chatControls.avatar.rootContainer.isVisible = true;
        chatControls.avatar.modelProfileImage.setSource(url, Env.lobby.gallery.defaultPreview.path);
        chatControls.unavailableAvatar.isVisible = false;
        chatControls.unavailableAvatarLetter.isVisible = false;
    }
    outputPinnedBroadcastManagerState(state, _isGuest, _reason) {
        const chatControls = this._controls.pinnedChatControls.chatControls;
        const avatarData = AvatarStatus.broadcastManagerStateMapForMessenger[state];
        chatControls.avatar.outlineContainer.color = avatarData.color;
        chatControls.avatar.statusTextContainer.background = avatarData.color;
        chatControls.avatar.statusText.text = avatarData.text;
    }
    _createContent(parent) {
        const background = this._createBackground(parent);
        this._createHeader(background);
        return this._createBody(background);
    }
    _createBackground(parent) {
        const cp = Env.common.messengerChatListPanel;
        const backgroundRectangle = new Rectangle3D("messengerChatListPanelBackgroundRectangle");
        backgroundRectangle.zIndex = -1;
        backgroundRectangle.thickness = 0;
        backgroundRectangle.cornerRadius = this._mesh2d.m2px(cp.background.cornerRadius);
        parent.addControl(backgroundRectangle);
        const backgroundImage = new Image3D("messengerChatListPanelBackgroundImage", cp.background.backgroundImage);
        backgroundImage.zIndex = -1;
        backgroundRectangle.addControl(backgroundImage);
        return backgroundRectangle;
    }
    _createHeader(parent) {
        const cp = Env.common.messengerChatListPanel;
        const headerBackgroundRectangle = new Rectangle3D("messengerChatListPanelHeaderBackground");
        headerBackgroundRectangle.thickness = 0;
        headerBackgroundRectangle.background = cp.header.background;
        headerBackgroundRectangle.heightInPixels = this._mesh2d.m2px(cp.header.height);
        headerBackgroundRectangle.verticalAlignment = Rectangle3D.VERTICAL_ALIGNMENT_TOP;
        parent.addControl(headerBackgroundRectangle);
        const captionTextBlock = ObjectBuilder.createTextBlock3D(this._mesh2d, "messengerChatListPanelCaption", text.common_messenger_chatlist_caption, cp.header.caption);
        headerBackgroundRectangle.addControl(captionTextBlock);
    }
    _createBody(parent) {
        const pinnedChatControls = this._createPinnedChatPanel();
        parent.addControl(pinnedChatControls.rootContainer);
        const chatListControls = this._createChatListPanel();
        parent.addControl(chatListControls.rootContainer);
        return {
            pinnedChatControls,
            chatListControls,
        };
    }
    _createPinnedChatPanel() {
        const pp = Env.common.messengerChatListPanel.body.pinnedPanel;
        const rootContainer = new Container("messengerChatListPanelPinnedModelPanel");
        rootContainer.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
        rootContainer.heightInPixels = this._mesh2d.m2px(pp.height);
        rootContainer.topInPixels = this._mesh2d.m2px(pp.top);
        const chatControls = MessengerChatListLayoutHelper.createChatView({ mesh2d: this._mesh2d });
        chatControls.rootContainer.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
        chatControls.rootContainer.topInPixels = this._mesh2d.m2px(pp.model.top);
        rootContainer.addControl(chatControls.rootContainer);
        chatControls.modelButton.onClickObserver.clear();
        chatControls.modelButton.onClickObserver.add(this._buttonHandler);
        const separator = new Rectangle3D("messengerChatListPanelPinnedModelPanelSeparator");
        separator.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
        separator.widthInPixels = this._mesh2d.m2px(pp.separator.width);
        separator.heightInPixels = this._mesh2d.m2px(pp.separator.height);
        separator.topInPixels = this._mesh2d.m2px(pp.separator.top);
        separator.thickness = 0;
        separator.background = pp.separator.color;
        rootContainer.addControl(separator);
        return {
            rootContainer,
            chatControls,
        };
    }
    _createChatListPanel() {
        const cl = Env.common.messengerChatListPanel.body.chatList;
        const sv = cl.scrollView;
        const scrollViewer = new ScrollViewer3D("messengerChatListPanelScrollViewer");
        scrollViewer.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        scrollViewer.heightInPixels = this._mesh2d.m2px(sv.fullHeight); // switches in outputMessengerCurrentChat()
        scrollViewer.color = sv.colors.background;
        scrollViewer.barColor = sv.colors.bar;
        scrollViewer.hoverBarColor = sv.colors.hoverBar;
        scrollViewer.pressedBarColor = sv.colors.pressedBar;
        scrollViewer.verticalBarPaddingInPixels = {
            left: this._mesh2d.m2px(sv.verticalBar.padding.left),
            right: this._mesh2d.m2px(sv.verticalBar.padding.right),
            top: this._mesh2d.m2px(sv.verticalBar.padding.top),
            bottom: this._mesh2d.m2px(sv.verticalBar.padding.bottom)
        };
        scrollViewer.barSizeInPixels = this._mesh2d.m2px(sv.verticalBar.size);
        const scrollHandler = new ScrollHandler([this._mesh2d.mesh.name], this._mesh2d.m2px(Env.broadcast.menu.chat.scrollView.speed), (v) => {
            scrollViewer.verticalValue = Math.max(Math.min(scrollViewer.verticalValue + v / (scrollViewer.children[0].heightInPixels - scrollViewer.heightInPixels), 1), 0);
        });
        const pointerAddon = XRInputHelper.addPointerAddon(this._mesh2d.mesh);
        pointerAddon.onPointerOut.add(() => {
            scrollHandler.stopScroll();
            this._mesh2d.notifyChildrenOnPointerOut();
        });
        scrollHandler.releaseThumbstickAxesChangedListener();
        scrollHandler.addThumbstickAxesChangedListener();
        const chatsStackPanel = new StackPanel("messengerPanelChatStackPanel");
        chatsStackPanel.isVertical = true;
        chatsStackPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        chatsStackPanel.spacing = this._mesh2d.m2px(cl.stackPanel.spacing);
        scrollViewer.addControl(chatsStackPanel);
        const topGap = new Container("messengerChatListPanelTopGap");
        topGap.widthInPixels = 1;
        topGap.heightInPixels = this._mesh2d.m2px(cl.stackPanel.topGapHeight);
        chatsStackPanel.addControl(topGap);
        const bottomGap = new Container("messengerChatListPanelBottomGap");
        bottomGap.widthInPixels = 1;
        bottomGap.heightInPixels = this._mesh2d.m2px(cl.stackPanel.bottomGapHeight);
        chatsStackPanel.addControl(bottomGap);
        return {
            rootContainer: scrollViewer,
            scrollViewer,
            scrollHandler,
            chatsStackPanel,
            topGap,
            bottomGap,
        };
    }
}
